/**
 * What the markdown editor's toolbar does to the text (spec 145 US1, US2).
 *
 * Pure: each command takes the text and the selection and returns the text and
 * selection it should become. The editor applies the result through the text
 * box's own editing, so the browser's undo holds each command as one step; this
 * file never touches the DOM, which is what lets every rule here be tested
 * without one.
 *
 * Not a markdown parser. A command looks at the characters around the
 * selection, the way a person reading the raw text would, and that is enough
 * for toggling what the same toolbar put there.
 */

/** Text with a selection; a caret is a selection whose ends meet. */
export interface TextEdit {
  text: string;
  selectionStart: number;
  selectionEnd: number;
}

/** The markers an inline command wraps a selection in. */
export type InlineMarker = '**' | '_' | '`';

/** The markers a line command puts at the start of each line. */
export type LinePrefix = 'bullet' | 'numbered' | 'quote';

const FENCE = '```';

/**
 * Wrap the selection in `marker`, or unwrap it when it is already wrapped —
 * pressing Bold twice is a round trip (US2 scenario 1).
 *
 * Whitespace at either end of the selection stays outside the markers: a
 * double-click often takes the space after a word, and `**word **` does not
 * render as bold. A caret gets an empty pair with the caret between, ready to
 * type into. Code over several lines becomes a fenced block, since a backtick
 * span cannot hold a line break.
 */
export function toggleInline(edit: TextEdit, marker: InlineMarker): TextEdit {
  const { text } = edit;
  const selected = text.slice(edit.selectionStart, edit.selectionEnd);
  const start =
    edit.selectionStart + (selected.length - selected.trimStart().length);
  const end = Math.max(
    start,
    edit.selectionEnd - (selected.length - selected.trimEnd().length),
  );
  const inner = text.slice(start, end);

  if (marker === '`' && inner.includes('\n')) {
    return toggleFence(text, start, end);
  }

  // Markers just outside the selection: `**|word|**`.
  if (
    end > start &&
    text.slice(start - marker.length, start) === marker &&
    text.slice(end, end + marker.length) === marker
  ) {
    return {
      text:
        text.slice(0, start - marker.length) +
        inner +
        text.slice(end + marker.length),
      selectionStart: start - marker.length,
      selectionEnd: end - marker.length,
    };
  }
  // Markers just inside it: `|**word**|`.
  if (
    inner.length >= marker.length * 2 + 1 &&
    inner.startsWith(marker) &&
    inner.endsWith(marker)
  ) {
    const unwrapped = inner.slice(marker.length, -marker.length);
    return {
      text: text.slice(0, start) + unwrapped + text.slice(end),
      selectionStart: start,
      selectionEnd: start + unwrapped.length,
    };
  }
  return {
    text: text.slice(0, start) + marker + inner + marker + text.slice(end),
    selectionStart: start + marker.length,
    selectionEnd: end + marker.length,
  };
}

/** A fenced block around whole lines, or the fences around them removed. */
function toggleFence(text: string, start: number, end: number): TextEdit {
  const inner = text.slice(start, end);
  const fenced = /^```[^\n]*\n([\s\S]*)\n```$/.exec(inner);
  if (fenced) {
    const body = fenced[1] ?? '';
    return {
      text: text.slice(0, start) + body + text.slice(end),
      selectionStart: start,
      selectionEnd: start + body.length,
    };
  }
  // A fence must open and close on lines of its own.
  const before = start > 0 && text[start - 1] !== '\n' ? '\n' : '';
  const after = end < text.length && text[end] !== '\n' ? '\n' : '';
  const open = `${before}${FENCE}\n`;
  return {
    text:
      text.slice(0, start) +
      open +
      inner +
      `\n${FENCE}${after}` +
      text.slice(end),
    selectionStart: start + open.length,
    selectionEnd: start + open.length + inner.length,
  };
}

const PREFIX_PATTERN: Record<LinePrefix, RegExp> = {
  bullet: /^[-*+] /,
  numbered: /^\d+[.)] /,
  quote: /^> ?/,
};

/** List markers, either kind — swapped out when a line becomes the other kind. */
const ANY_LIST = /^([-*+]|\d+[.)]) /;

/**
 * Give each line the selection touches the `prefix`, or take it away when
 * every one already has it (US2 scenario 2). With some lines prefixed and some
 * not, it is added to the ones that lack it and never doubled on the others.
 *
 * Bulleted and numbered replace each other rather than stacking: pressing
 * Numbered on a bulleted list renumbers it. A quote stacks on a list item,
 * which is what quoting a list means. Blank lines inside a selection of
 * several are left blank, so a list does not grow empty items; a single blank
 * line under the caret is where a list is being started, and gets the marker.
 *
 * A caret stays where it was in the words; a selection grows to cover the
 * lines it changed, so pressing the button again acts on the same lines.
 */
export function toggleLinePrefix(edit: TextEdit, prefix: LinePrefix): TextEdit {
  const { text } = edit;
  const lineStart = text.lastIndexOf('\n', edit.selectionStart - 1) + 1;
  // A selection ending at the very start of a line (a triple-click takes the
  // newline) does not include that line.
  const endAt =
    edit.selectionEnd > edit.selectionStart &&
    text[edit.selectionEnd - 1] === '\n'
      ? edit.selectionEnd - 1
      : edit.selectionEnd;
  const nextBreak = text.indexOf('\n', endAt);
  const lineEnd = nextBreak === -1 ? text.length : nextBreak;
  const lines = text.slice(lineStart, lineEnd).split('\n');
  const several = lines.length > 1;
  const counted = (line: string) => !several || line.trim().length > 0;

  const pattern = PREFIX_PATTERN[prefix];
  const removing = lines.filter(counted).every((line) => pattern.test(line));

  let number = 0;
  const changed = lines.map((line) => {
    if (!counted(line)) return line;
    if (removing) return line.replace(pattern, '');
    number += 1;
    if (prefix === 'quote') return pattern.test(line) ? line : `> ${line}`;
    const bare = line.replace(ANY_LIST, '');
    return prefix === 'bullet' ? `- ${bare}` : `${String(number)}. ${bare}`;
  });
  const block = changed.join('\n');
  const next = text.slice(0, lineStart) + block + text.slice(lineEnd);

  if (edit.selectionStart === edit.selectionEnd && !several) {
    const shift = block.length - (lineEnd - lineStart);
    const caret = Math.max(lineStart, edit.selectionStart + shift);
    return { text: next, selectionStart: caret, selectionEnd: caret };
  }
  return {
    text: next,
    selectionStart: lineStart,
    selectionEnd: lineStart + block.length,
  };
}

/**
 * Replace the selection with `[label](url)` and put the caret after it (US1
 * scenario 2). An empty label reads as the address itself. Brackets in the
 * label are escaped, and spaces and parentheses in the address encoded, so
 * neither can end the link early; this is the one change made to an address
 * the form accepted. Control characters (a line break above all) are dropped
 * from the address, since one would end the link as surely, whatever let it
 * through.
 */
export function insertLink(
  edit: TextEdit,
  label: string,
  url: string,
): TextEdit {
  const words = label.trim() === '' ? url : label;
  const link = `[${escapeLabel(words)}](${encodeTarget(url)})`;
  const caret = edit.selectionStart + link.length;
  return {
    text:
      edit.text.slice(0, edit.selectionStart) +
      link +
      edit.text.slice(edit.selectionEnd),
    selectionStart: caret,
    selectionEnd: caret,
  };
}

function escapeLabel(label: string): string {
  return label.replace(/[[\]\\]/g, '\\$&').replace(/\n+/g, ' ');
}

function encodeTarget(url: string): string {
  return withoutControls(url)
    .replace(/ /g, '%20')
    .replace(/\(/g, '%28')
    .replace(/\)/g, '%29');
}

/** `text` without C0 control characters or DEL. */
function withoutControls(text: string): string {
  return Array.from(text)
    .filter((char) => !isControl(char))
    .join('');
}

function isControl(char: string): boolean {
  const code = char.charCodeAt(0);
  return code < 0x20 || code === 0x7f;
}

const ALLOWED_SCHEME = /^(https?:\/\/|mailto:)/i;
/** A host with a dot, or `localhost`, an optional port, and anything after. */
const BARE_HOST =
  /^(localhost|[\p{L}\p{N}-]+(\.[\p{L}\p{N}-]+)+)(:\d+)?([/?#]\S*)?$/u;
const EMAIL = /^[^\s@/:]+@[^\s@/:]+\.[^\s@/:]+$/;

/**
 * The address a link may go to, or `null` when it may not (spec 145 FR-003).
 *
 * Only `http`, `https` and `mailto`, the same three the renderer will make
 * into links, so the form cannot write a link that then shows as text. A bare
 * host (`example.com/x`) is offered as `https://example.com/x`, and a bare
 * email address as `mailto:`. A long address is kept whole. One holding a
 * control character, a line break inside it say, is refused: the browser's
 * parser would quietly drop it, and the address would not be the one typed.
 */
export function normaliseUrl(input: string): string | null {
  const url = input.trim();
  if (url === '' || Array.from(url).some(isControl)) return null;
  if (/^mailto:/i.test(url)) return url.length > 'mailto:'.length ? url : null;
  if (ALLOWED_SCHEME.test(url)) return hasHost(url) ? url : null;
  // Checked before refusing other schemes: `localhost:8080/x` is a host and a
  // port, not a scheme called `localhost`.
  if (BARE_HOST.test(url)) return `https://${url}`;
  if (EMAIL.test(url)) return `mailto:${url}`;
  // Anything else, `javascript:` and `ftp:` included.
  return null;
}

function hasHost(url: string): boolean {
  try {
    return new URL(url).host.length > 0;
  } catch {
    return false;
  }
}

/**
 * Whether pasted text is an address to link the selection to (US1 scenario
 * 4). Stricter than the form: only an address with its scheme written out, on
 * its own. Pasting `example.com` over a word is as likely to mean "replace the
 * word" as "link it", and the paste then does what it always did.
 */
export function pastedAddress(pasted: string): string | null {
  const url = pasted.trim();
  if (/\s/.test(url) || !ALLOWED_SCHEME.test(url)) return null;
  return normaliseUrl(url);
}
